/**
 * Condition 評価器（schema/app-definition.ts の Condition と 1:1）
 * - eval / new Function / 動的コードは一切使わない
 * - 構造化条件（all/any/not/比較）＋ 式（{expr} / D-0009）に対応
 * - サーバ(Worker)・クライアント共通で使える純関数
 */
import { evaluateExpression, truthy } from "./expression.mjs";

/** @typedef {{ userId?: string, now?: Date }} EvalContext */

/** 比較値を解決する。 */
function resolveValue(valueSpec, row, ctx) {
  if (valueSpec == null) return undefined;
  if ("literal" in valueSpec) return valueSpec.literal;
  if ("columnId" in valueSpec) return row[valueSpec.columnId];
  if ("context" in valueSpec) {
    const now = ctx?.now ?? new Date();
    switch (valueSpec.context) {
      case "currentUser": return ctx?.userId;
      case "now": return now.toISOString();
      case "today": return now.toISOString().slice(0, 10);
      default: return undefined;
    }
  }
  return undefined;
}

function compare(cmp, row, ctx) {
  const left = row[cmp.columnId];
  const right = resolveValue(cmp.value, row, ctx);
  switch (cmp.op) {
    case "eq": return left === right;
    case "neq": return left !== right;
    case "gt": return left > right;
    case "gte": return left >= right;
    case "lt": return left < right;
    case "lte": return left <= right;
    case "contains": return String(left ?? "").includes(String(right ?? ""));
    case "startsWith": return String(left ?? "").startsWith(String(right ?? ""));
    case "endsWith": return String(left ?? "").endsWith(String(right ?? ""));
    case "in": return Array.isArray(right) && right.includes(left);
    case "isEmpty": return left == null || left === "";
    case "isNotEmpty": return left != null && left !== "";
    default:
      throw new Error(`Unknown op: ${cmp.op}`);
  }
}

/**
 * Condition を評価する。
 * @param {object|undefined} cond - Condition（undefined は「常に真」）
 * @param {Record<string, unknown>} row
 * @param {EvalContext} [ctx]
 * @returns {boolean}
 */
export function evaluateCondition(cond, row, ctx) {
  if (cond == null) return true;
  if ("all" in cond) return cond.all.every((c) => evaluateCondition(c, row, ctx));
  if ("any" in cond) return cond.any.some((c) => evaluateCondition(c, row, ctx));
  if ("not" in cond) return !evaluateCondition(cond.not, row, ctx);
  if ("expr" in cond) return truthy(evaluateExpression(cond.expr, row, ctx)); // 式条件（D-0009）
  return compare(cond, row, ctx);
}
